import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'wouter';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { motion } from 'framer-motion';
import { CheckCircle, MapPin, ClipboardList, Users, Calendar, ArrowRight } from 'lucide-react';

export default function RoofingSoftwarePage() {
  return (
    <>
      <Helmet>
        <title>Roofing Contractor Software with AI Roof Measurement – Remodra</title>
        <meta
          name="description"
          content="Roofing contractor software with built-in AI roof measurement. Create estimates, invoices, and projects faster using address-only roof reports inside Remodra."
        />
        <link rel="canonical" href="https://getremodra.com/roofing-software" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Roofing Contractor Software with AI Roof Measurement – Remodra" />
        <meta
          property="og:description"
          content="All-in-one roofing software with AI roof measurement from just an address. Estimates, invoices, projects, calendar, and client portal in one place."
        />
        <meta property="og:url" content="https://getremodra.com/roofing-software" />
        <meta property="og:image" content="https://getremodra.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Roofing Contractor Software with AI Roof Measurement – Remodra" />
        <meta
          name="twitter:description"
          content="All-in-one roofing software with AI roof measurement from just an address. Estimates, invoices, projects, and client communication."
        />
        <meta name="twitter:image" content="https://getremodra.com/og-image.png" />
      </Helmet>
      <div className="min-h-screen bg-slate-50 text-slate-900">
        <header className="border-b border-slate-200 bg-white/80 backdrop-blur">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 md:h-20">
            <Link href="/">
              <a className="flex items-center space-x-2">
                <img src="/remodra-logo.png" alt="Remodra" className="h-9 w-9 rounded-lg object-cover" />
                <span className="text-xl font-bold tracking-tight text-slate-900">Remodra</span>
              </a>
            </Link>
            <div className="flex items-center gap-3">
              <Link href="/pricing">
                <Button variant="ghost" className="text-slate-600">
                  Pricing
                </Button>
              </Link>
              <Link href="/register?plan=pro">
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl">
                  Start free trial
                </Button>
              </Link>
            </div>
          </div>
        </header>

        <main>
          {/* HERO WITH ANGLED APP PREVIEW */}
          <section className="bg-gradient-to-b from-emerald-50 via-slate-50 to-white">
            <div className="container mx-auto px-4 py-16 md:py-24 max-w-6xl grid lg:grid-cols-[1.1fr,1.1fr] gap-10 items-center">
              <Badge className="bg-emerald-500/10 text-emerald-700 border-emerald-400/40 mb-4">
                Built for roofing contractors
              </Badge>
              <div>
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 mb-4">
                  Roofing contractor software with built‑in AI roof measurement
                </h1>
                <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mb-8">
                  Show homeowners a modern roofing company. Remodra combines AI roof reports, estimates, invoices, and
                  projects in one clean dashboard.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Link href="/register?plan=pro">
                    <Button className="px-8 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold shadow-lg flex items-center">
                      Start Pro trial for roofers
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                  <Link href="/schedule-demo">
                    <Button variant="outline" className="px-8 py-3 rounded-xl border-emerald-600 text-emerald-700 font-medium">
                      See a live roofing demo
                    </Button>
                  </Link>
                </div>
                <ul className="grid sm:grid-cols-2 gap-3 text-sm text-slate-700">
                  <li className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 mt-1 text-emerald-600" />
                    <span>AI roof measurement from just an address — no ladder, no photos.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ClipboardList className="h-4 w-4 mt-1 text-emerald-600" />
                    <span>AI‑written roofing estimate descriptions that win more bids.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Calendar className="h-4 w-4 mt-1 text-emerald-600" />
                    <span>Calendar, projects, and time tracking for every crew.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Users className="h-4 w-4 mt-1 text-emerald-600" />
                    <span>Client portal so homeowners can review, approve, and pay online.</span>
                  </li>
                </ul>
              </div>

              <div className="relative hidden md:block">
                <motion.div
                  className="relative w-full max-w-xl mx-auto group"
                  initial={{ opacity: 0, y: 40, rotate: -4, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, rotate: -4, scale: 1 }}
                  transition={{ duration: 0.8, ease: 'easeOut' }}
                  whileHover={{ rotate: -2, y: -4, scale: 1.01 }}
                >
                  <div className="absolute -inset-6 bg-gradient-to-tr from-emerald-500/25 via-teal-500/15 to-transparent rounded-3xl blur-xl group-hover:blur-2xl transition-all" />
                  <div className="relative rounded-3xl overflow-hidden border border-emerald-100/80 shadow-2xl shadow-emerald-500/30 bg-slate-900/5 backdrop-blur-sm">
                    <img
                      src="/roof-measure.png"
                      alt="Remodra AI roof measurement — Analysis tab with aerial report by address"
                      className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                    />
                  </div>
                </motion.div>
                <motion.div
                  className="hidden lg:block absolute -bottom-10 -left-4 w-64"
                  initial={{ opacity: 0, y: 30, rotate: 10, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, rotate: 10, scale: 1 }}
                  transition={{ delay: 0.25, duration: 0.8, ease: 'easeOut' }}
                  whileHover={{ rotate: 6, y: -2, scale: 1.02 }}
                >
                  <div className="rounded-3xl overflow-hidden border border-emerald-100 shadow-xl shadow-emerald-500/25 bg-slate-900/5 backdrop-blur-sm">
                    <img
                      src="/roof-measure-waste.png"
                      alt="Remodra AI roof measurement — Waste tab with squares to order"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </motion.div>
              </div>
            </div>
          </section>

          {/* SCREENSHOTS STRIP: PROJECTS / INVOICES */}
          <section className="py-16 md:py-20 bg-white">
            <div className="container mx-auto px-4 max-w-6xl">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-10">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
                    See every roofing job at a glance
                  </h2>
                  <p className="text-sm md:text-base text-slate-600 max-w-xl">
                    From first call to final invoice, Remodra keeps all your roofing estimates, projects, and invoices
                    in one clean workspace your team can actually use.
                  </p>
                </div>
                <div className="flex gap-3 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1">
                    <CheckCircle className="h-3 w-3 text-emerald-600" /> Projects
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <CheckCircle className="h-3 w-3 text-emerald-600" /> Estimates
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <CheckCircle className="h-3 w-3 text-emerald-600" /> Invoices
                  </span>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <motion.div
                  className="rounded-2xl overflow-hidden border border-slate-200/80 bg-slate-900/5 shadow-lg hover:shadow-2xl transition-transform duration-500"
                  initial={{ opacity: 0, y: 20, rotate: -3 }}
                  whileInView={{ opacity: 1, y: 0, rotate: -3 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                  whileHover={{ y: -6, rotate: -1, scale: 1.02 }}
                >
                  <img src="/projects.png" alt="Remodra projects list" className="w-full h-auto object-cover" />
                </motion.div>
                <motion.div
                  className="rounded-2xl overflow-hidden border border-slate-200/80 bg-slate-900/5 shadow-lg hover:shadow-2xl transition-transform duration-500"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1, ease: 'easeOut' }}
                  whileHover={{ y: -6, scale: 1.02 }}
                >
                  <img src="/estimates.png" alt="Remodra estimates list" className="w-full h-auto object-cover" />
                </motion.div>
                <motion.div
                  className="rounded-2xl overflow-hidden border border-slate-200/80 bg-slate-900/5 shadow-lg hover:shadow-2xl transition-transform duration-500"
                  initial={{ opacity: 0, y: 20, rotate: 3 }}
                  whileInView={{ opacity: 1, y: 0, rotate: 3 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2, ease: 'easeOut' }}
                  whileHover={{ y: -6, rotate: 1, scale: 1.02 }}
                >
                  <img src="/invoices.png" alt="Remodra invoices list" className="w-full h-auto object-cover" />
                </motion.div>
              </div>
            </div>
          </section>

          {/* BENEFITS GRID */}
          <section className="py-12 md:py-16 bg-slate-50">
            <div className="container mx-auto px-4 max-w-5xl">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
                Why roofing contractors choose Remodra
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  {
                    title: 'Faster, accurate quotes',
                    text: 'Use AI roof reports and AI descriptions to send detailed roofing estimates in minutes, not hours.',
                  },
                  {
                    title: 'All jobs in one board',
                    text: 'Track inspections, repairs, and full replacements on simple Kanban boards your team actually uses.',
                  },
                  {
                    title: 'Look more professional',
                    text: 'Modern PDFs, branded emails, and a secure client portal make your company stand out immediately.',
                  },
                ].map((item) => (
                  <div key={item.title} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                    <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-slate-600">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-16 md:py-20 bg-slate-50">
            <div className="container mx-auto px-4 max-w-4xl">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">FAQ for roofing teams</h2>
              <div className="space-y-6 text-sm text-slate-700">
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Does Remodra only work for roofing?</h3>
                  <p>
                    No. Remodra is built for all types of contractors, but has a dedicated AI roof measurement tool and
                    workflows that roofing companies love.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">
                    Do I need photos or site visits to get roof measurements?
                  </h3>
                  <p>
                    For AI roof reports, you only need the property address. Remodra uses aerial and satellite data to
                    estimate square footage, pitch, and sections.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Which plan includes AI roof reports?</h3>
                  <p>
                    AI roof measurement is included in Pro and Business plans with a monthly bundle of reports, plus a
                    <span className="line-through">simple per‑report price if you need more</span>, now completely free.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>
    </>
  );
}

